import { useEffect, useRef } from 'react';

import { Dialog, IconButton } from '@mui/material';
import { CloseIcon } from '@truvhq/ui';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { useOnboardingState } from 'entities/onboarding/api/useOnboardingState';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { OnboardingConversation } from 'features/onboarding/ui/OnboardingConversation';
import { useAuth } from 'shared/lib/auth/useAuth';
import { FeatureFlag } from 'shared/lib/features/common';

const SESSION_FLAG = 'truv-onboarding-auto-opened';

// Mounts the conversational onboarding as a global popup driven by useOnboardingDialogStore.
// Auto-opens once per browser session (via sessionStorage, so it clears on tab close) for
// authenticated users in the assistant rollout; can also be opened imperatively via the store.
export function OnboardingDialog() {
  const { user } = useAuth();
  const assistantEnabled = useFeatureFlagEnabled(FeatureFlag.AI_ASSISTANT);
  const isOpen = useOnboardingDialogStore((state) => state.isOpen);
  const sessionKey = useOnboardingDialogStore((state) => state.sessionKey);
  const open = useOnboardingDialogStore((state) => state.open);
  const close = useOnboardingDialogStore((state) => state.close);
  const autoOpenedRef = useRef(false);

  useEffect(() => {
    if (autoOpenedRef.current || !user || !assistantEnabled || typeof window === 'undefined') {
      return;
    }
    if (window.sessionStorage.getItem(SESSION_FLAG)) {
      return;
    }
    window.sessionStorage.setItem(SESSION_FLAG, '1');
    autoOpenedRef.current = true;
    open();
  }, [user, assistantEnabled, open]);

  // Render mode comes from constance (via the onboarding state endpoint): "docked" is a right-side
  // panel that leaves the dashboard interactive; anything else (incl. while loading) is the centered
  // window. Only fetched once the dialog opens.
  const { data: onboardingState } = useOnboardingState({ enabled: isOpen });
  const isDocked = onboardingState?.layout === 'docked';

  const closeButton = (
    <IconButton
      aria-label="Close onboarding"
      onClick={close}
      sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: 'text.secondary' }}
    >
      <CloseIcon />
    </IconButton>
  );
  const conversation = (
    <div className="h-full overflow-hidden">
      <OnboardingConversation key={sessionKey} />
    </div>
  );

  if (isDocked) {
    return (
      isOpen && (
        <div
          role="dialog"
          aria-label="Get started"
          className="fixed inset-y-0 right-0 z-[1300] flex w-[min(30rem,100vw)] flex-col overflow-hidden border-grey30 border-l bg-white shadow-2xl"
        >
          {closeButton}
          {conversation}
        </div>
      )
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      aria-label="Get started"
      maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 720, height: '85vh', borderRadius: '12px' } }}
    >
      {closeButton}
      {conversation}
    </Dialog>
  );
}
